<template>
  <v-card>
    <v-toolbar dense color="grey lighten-3" flat>
      <v-icon v-if="newMtrNCD">mdi-plus</v-icon>
      <v-icon v-else>mdi-pencil</v-icon>
      <v-toolbar-title v-if="newMtrNCD">Create New Motor NCD Rate</v-toolbar-title>
      <v-toolbar-title v-else>Edit Motor NCD Rate</v-toolbar-title>
    </v-toolbar>
    <v-divider></v-divider>
    <v-card-text>
      <message :messages="messages"></message>
      <form>
        <v-container fluid grid-list-lg>
          <v-layout row wrap>
            <v-flex xs6 v-if="newMtrNCD">
              <v-autocomplete
                :items="tariffList"
                v-model="mtrNCDForm.tarf"
                :label="labels.tarf"
                item-text="label"
                item-value="value"
                data-vv-name="tarf"
                :data-vv-as="labels.tarf"
                :error-messages="errors.collect('tarf')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{ labels.tarf }}</v-subheader>
              <div class="queryValue">{{ getDesc('tarf', mtrNCDForm.tarf) }}</div>
            </v-flex>
            <v-flex xs6 v-if="newMtrNCD">
              <v-autocomplete
                :items="busClsTarfList"
                v-model="mtrNCDForm.busCls"
                :label="labels.busCls"
                item-text="label"
                item-value="value"
                data-vv-name="busCls"
                :data-vv-as="labels.busCls"
                :error-messages="errors.collect('busCls')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{ labels.busCls }}</v-subheader>
              <div class="queryValue">{{ getDesc('busCls', mtrNCDForm.busCls) }}</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6 v-if="newMtrNCD">
              <v-autocomplete
                :items="busClsCvrList"
                v-model="mtrNCDForm.cvrType"
                :label="labels.cvrType"
                item-text="label"
                item-value="value"
                data-vv-name="cvrType"
                :data-vv-as="labels.cvrType"
                :error-messages="errors.collect('cvrType')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{ labels.cvrType }}</v-subheader>
              <div class="queryValue">{{ getDesc('cvrType', mtrNCDForm.cvrType) }}</div>
            </v-flex>
            <v-flex xs6 v-if="newMtrNCD">
              <v-autocomplete
                :items="ncbYrList"
                v-model="mtrNCDForm.ncbYr"
                :label="labels.ncbYr"
                item-text="label"
                item-value="value"
                data-vv-name="ncbYr"
                :data-vv-as="labels.ncbYr"
                :error-messages="errors.collect('ncbYr')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{ labels.ncbYr }}</v-subheader>
              <div class="queryValue">{{ mtrNCDForm.ncbYr }}</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6 v-if="newMtrNCD">
              <cal-picker
                v-model="mtrNCDForm.effDate"
                :label="labels.effDate"
                :data-vv-as="labels.effDate"
                data-vv-name="effDate"
                :min="today"
                :error-messages="errors.collect('effDate')"
                v-validate="'required|checkEffDate'"
              ></cal-picker>
            </v-flex>
            <v-flex xs6 v-else>
              <v-subheader class="queryLabel">{{ labels.effDate }}</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(mtrNCDForm.effDate) }}</div>
            </v-flex>
            <v-flex xs6>
              <v-text-field
                v-model="mtrNCDForm.ncbPct"
                v-num-mask="{ negative: false, intScale: 3, decScale: 2 }"
                :label="labels.ncbPct"
                :error-messages="errors.collect('ncbPct')"
                data-vv-name="ncbPct"
                :data-vv-as="labels.ncbPct"
                v-validate="'required'"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6>
              <v-autocomplete
                :items="permitStatusList"
                v-model="mtrNCDForm.permitStatus"
                :label="labels.permitStatus"
                item-text="label"
                item-value="value"
                data-vv-name="permitStatus"
                :data-vv-as="labels.permitStatus"
                :error-messages="errors.collect('permitStatus')"
                v-validate="'required'"
              ></v-autocomplete>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6>
              <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
              <div class="queryValue">{{ mtrNCDForm.createdName }}</div>
            </v-flex>
            <v-flex xs6>
              <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(mtrNCDForm.createdDate) }}</div>
            </v-flex>
            <v-flex xs6 v-if="!newMtrNCD">
              <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
              <div class="queryValue">{{ mtrNCDForm.modifiedName }}</div>
            </v-flex>
            <v-flex xs6 v-if="!newMtrNCD">
              <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(mtrNCDForm.modifiedDate) }}</div>
            </v-flex>
          </v-layout>
        </v-container>
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="red darken-1" text @click.native="close">Cancel</v-btn>
      <v-btn color="primary" text @click.native="doSave">{{ newMtrNCD ? 'Save' : 'Save' }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { formatDisplayDate, removeTime } from '@/utils/date'
import isAfter from 'date-fns/isAfter'
import { expandURI } from '@/utils/ajax'

export default {
  props: {
    mtrNCDForm: Object,
    newMtrNCD: Boolean
  },
  data() {
    return {
      labels: {
        tarf: 'Tariff',
        busCls: 'Business Class',
        cvrType: 'Cover Type',
        ncbYr: 'NCB Years',
        effDate: 'Effective Date',
        ncbPct: 'NCB Rate',
        permitStatus: 'Permit Status',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      today: new Date(),
      tariffList: [],
      busClsList: [],
      cvrTypeList: [],
      busClsTarfList: [],
      busClsCvrList: [],
      loading: false,
      submitting: false
    }
  },
  watch: {
    'mtrNCDForm.tarf': function (value) {
      this.getBusClsListByTarf()
    },
    'mtrNCDForm.busCls': function (value) {
      this.getCoverListByBusCls()
    }
  },
  computed: {
    menu() {
      return this.$store.state.uwMotorParam.mtrNCD.menu
    },
    permitStatusList() {
      return [
        { label: 'Accepted', value: 'A' },
        { label: 'Declined', value: 'D' },
        { label: 'Referred', value: 'R' }
      ]
    },
    ncbYrList() {
      return [
        { label: '0', value: 0 },
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
        { label: '5', value: 5 }
      ]
    }
  },
  methods: {
    doInit() {
      this.resetMessages()
      this.getTariffList()
      this.getBusClsList()
      this.getCoverList()
      if (this.newMtrNCD) {
        this.doInitForm()
      } else {
        this.getBusClsListByTarf()
        this.getCoverListByBusCls()
      }
    },
    doInitForm() {
      this.resetValidator()
      this.resetMessages()
      this.mtrNCDForm.tarf = undefined
      this.mtrNCDForm.busCls = null
      this.mtrNCDForm.cvrType = null
      this.mtrNCDForm.ncbYr = null
      this.mtrNCDForm.effDate = new Date()
      this.mtrNCDForm.ncbPct = 0
      this.mtrNCDForm.permitStatus = ''
      this.mtrNCDForm.createdName = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
      this.mtrNCDForm.createdDate = new Date()
    },
    formatDisplayDate(xDate) {
      if (xDate) {
        return formatDisplayDate(xDate)
      } else {
        return undefined
      }
    },
    getDesc(field, id) {
      var list = []
      if (field === 'tarf') {
        list = this.tariffList
      } else if (field === 'busCls') {
        list = this.busClsList
      } else {
        list = this.cvrTypeList
      }
      if (id) {
        for (var i in list) {
          if (id === list[i].value) {
            return list[i].label
          }
        }
      }
    },
    async getTariffList() {
      const apiURI = this.menu.getTariffList
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        this.tariffList = []
        if (data) {
          this.tariffList = data
        }
      }
    },
    async getBusClsList() {
      const apiURI = this.menu.getBusClsList
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        this.busClsList = []
        if (data) {
          this.busClsList = data
        }
      }
    },
    async getCoverList() {
      const apiURI = this.menu.getCvrTypeList
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        this.cvrTypeList = []
        if (data) {
          this.cvrTypeList = data
        }
      }
    },
    async getBusClsListByTarf() {
      const apiURI = this.menu.getBusClsListByTarf
      if (apiURI) {
        const expand = { tarf: this.mtrNCDForm.tarf }
        const result = await this.$http.get(expandURI(apiURI, expand))
        const { data } = result.data
        this.busClsTarfList = []
        if (data) {
          this.busClsTarfList = data
        }
      }
    },
    async getCoverListByBusCls() {
      const apiURI = this.menu.getCoverListByBusCls
      if (apiURI) {
        const expand = { busCls: this.mtrNCDForm.busCls }
        const result = await this.$http.get(expandURI(apiURI, expand))
        const { data } = result.data
        this.busClsCvrList = []
        if (data) {
          this.busClsCvrList = data
        }
      }
    },
    doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newMtrNCD) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.createMtrNCD
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.mtrNCDForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.close()
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
        this.submitting = false
      }
    },
    async doUpdate() {
      const apiURI = this.menu.updateMtrNCD
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.mtrNCDForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.close()
          }
          this.submitting = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
        this.submitting = false
      }
    },
    close() {
      this.doInitForm()
      this.$emit('close')
    }
  },
  created() {
    this.doInit()
    const checkEffDate = {
      getMessage: (field, args) => {
        return 'Effective date cannot be a past date'
      },
      validate: (value, args) => {
        if (value) {
          return isAfter(removeTime(value), removeTime(new Date())) || removeTime(value) >= removeTime(new Date())
        } else {
          return true
        }
      }
    }
    this.$validator.extend('checkEffDate', checkEffDate)
  },
  mixins: [MessageMixin]
}
</script>
